import { apiFetch } from './client';
import { parseJsonResponse } from './parseResponse';

export type PlatformRole = {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  color: string;
  is_system: boolean;
  permissions: string[];
  users_count?: number;
  created_at?: string | null;
  updated_at?: string | null;
};

export type PlatformPermission = {
  id: number;
  key: string;
  name: string;
  module: string;
  sort_order: number;
};

export async function fetchPlatformRoles(): Promise<
  { ok: true; roles: PlatformRole[] } | { ok: false; message: string; unauthorized?: boolean }
> {
  const res = await apiFetch('/platform/roles');
  const out = await parseJsonResponse<PlatformRole[]>(res);
  if (out.ok === false) return { ok: false, message: out.message, unauthorized: out.unauthorized };
  return { ok: true, roles: Array.isArray(out.data) ? out.data : [] };
}

export async function fetchPlatformPermissionsCatalog(): Promise<
  | { ok: true; permissions: PlatformPermission[]; keys: string[] }
  | { ok: false; message: string; unauthorized?: boolean }
> {
  const res = await apiFetch('/platform/permissions');
  const out = await parseJsonResponse<{ permissions: PlatformPermission[]; keys: string[] }>(res);
  if (out.ok === false) return { ok: false, message: out.message, unauthorized: out.unauthorized };
  return {
    ok: true,
    permissions: out.data?.permissions ?? [],
    keys: out.data?.keys ?? [],
  };
}

export async function createPlatformRole(payload: {
  name: string;
  description?: string;
  color?: string;
  permissions: string[];
}): Promise<{ ok: true; role: PlatformRole } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch('/platform/roles', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const out = await parseJsonResponse<PlatformRole>(res);
  if (out.ok === false) return { ok: false, message: out.message, unauthorized: out.unauthorized };
  return { ok: true, role: out.data };
}

export async function updatePlatformRole(
  id: number,
  payload: { name?: string; description?: string; color?: string; permissions?: string[] },
): Promise<{ ok: true; role: PlatformRole } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/roles/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const out = await parseJsonResponse<PlatformRole>(res);
  if (out.ok === false) return { ok: false, message: out.message, unauthorized: out.unauthorized };
  return { ok: true, role: out.data };
}

export async function deletePlatformRole(
  id: number,
): Promise<{ ok: true } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/roles/${id}`, { method: 'DELETE' });
  const out = await parseJsonResponse<null>(res);
  if (out.ok === false) return { ok: false, message: out.message, unauthorized: out.unauthorized };
  return { ok: true };
}
